/* eslint @typescript-eslint/no-explicit-any: 0 */
import {Dispatch, useState, useEffect, useRef, useCallback} from 'react';

import {Box, Button, Card, Grid, makeStyles, Paper, Tooltip, Typography} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import {observer} from 'mobx-react-lite';
import {useHistory, useLocation} from 'react-router-dom';
import CustomerPortalStore from '../Customer/CustomerPortalStore/CustomerPortalStore';
import {
    AccountType,
    PaymentInformation,
    RequirementPaymentType,
    RequirementStatus,
    underwritingRequirementType,
} from '../types';
import {BankingRequirementTypeOptions} from './SelectOptions';
import RootStore from '../../Core/mobx/RootStore';
import AllStatus from '../Underwritings/AllStatus';
import CustomerPortalAPI from '../../APIs/CustomerPortalAPI';
import BankingInformationForm from './BankingInformationForm';
import BankTooltip from './BankTooltip';
import CaseManagerMenu from './CaseManagerMenu';
import EFTDocumentForm from './EFTDocumentForm';

interface BankingPanelProps {
    customerPortalStore: CustomerPortalStore;
}

interface BankingRequirementProps {
    index: number;
    value: any;
    arrayUpdateFunction: Dispatch<any>;
}

const useStyles = makeStyles({
    wrapper: {},
    requirementLabel: {
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: '1rem',
        lineHeight: '23px',
        color: '#000000',
    },
    requirementAddButton: {
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: '16px',
        textAlign: 'center',
        textTransform: 'uppercase',
        color: '#3B58B8',
    },
    requirementPanelRight: {
        scrollBehavior: 'smooth',
        overflowY: 'auto',
        height: 'calc(100vh - 300px)',
    },
});

const BankingPanel = observer(({customerPortalStore}: BankingPanelProps) => {
    const [requirementPaymentInfoArray, setRequirementPaymentInfoArray] = useState([] as any[]);
    const [localRequirementPaymentInfoArray, setLocalRequirementPaymentInfoArray] = useState([] as any[]);
    const [generalPaymentInfo, setGeneralPaymentInfo] = useState<PaymentInformation>({} as PaymentInformation);
    const [redirectUrl, setRedirectUrl] = useState(null as string | null);
    const docusignModalRequirement = useRef({} as any);
    const [updateInfo, setUpdateInfo] = useState(false);
    const classes = useStyles();
    const {search} = useLocation();
    const history = useHistory();
    const location = useLocation();

    const {selectedCustomer} = customerPortalStore;

    const [firstRender, setFirstRender] = useState(true);
    const ref = useRef<Array<HTMLDivElement | null>>([]);

    const formatDate = (dateStr: string) => {
        // modify string to add UTC timezone since we store without a timezone
        const date = new Date(`${dateStr}+00:00`);
        return date.toLocaleString().replace(',', ' -');
    };

    async function loadGeneralPaymentInformation() {
        if (selectedCustomer?.id) {
            try {
                const paymentInfo = await CustomerPortalAPI.GetCustomerGeneralPaymentInfo(selectedCustomer?.id);
                setGeneralPaymentInfo(paymentInfo || ({} as PaymentInformation));
                RootStore.uiStore.showSnackbar(
                    `Successfully ${firstRender ? 'Loaded' : 'Updated'} General Payment Info`,
                    'success',
                );
                setFirstRender(false);
            } catch (error: any) {
                console.log(error);
                RootStore.uiStore.showSnackbar('Something went wrong, Check Logs', 'error');
            }
        }
    }

    async function upsertBankingInfo(value: PaymentInformation) {
        try {
            if (!selectedCustomer?.id) {
                RootStore.uiStore.showSnackbar('Customer is not associated, Please select the customer', 'error');
                return null;
            }

            // ensure the field defaults to checking when not clicked, as this isn't done in the DB
            if (!value.accountType) {
                /* eslint-disable no-param-reassign */
                value.accountType = AccountType.CHECKING;
            }

            return value.id
                ? CustomerPortalAPI.UpdateCustomerPaymentInformation(selectedCustomer?.id, value)
                : CustomerPortalAPI.CreateCustomerPaymentInformation(selectedCustomer?.id, value);
        } catch (error: any) {
            console.log(error);
            RootStore.uiStore.showSnackbar('Something went wrong, Check Logs', 'error');
            return null;
        }
    }

    async function upsertGeneralPaymentInfo({
        childVal,
        setLoading,
    }: {
        childVal: PaymentInformation;
        setLoading: (loading: boolean) => void;
    }) {
        upsertBankingInfo(childVal).finally(() => {
            setLoading(false);
            loadGeneralPaymentInformation();
        });
    }

    /* eslint-disable @typescript-eslint/no-shadow */
    async function upsertRequirementAndUpdateArrays(
        idx: number | null,
        value: any,
        arrayUpdateFunction: ({idx, value}: {idx: number; value: any}) => void,
    ) {
        try {
            if (!selectedCustomer?.id) {
                RootStore.uiStore.showSnackbar('Customer is not associated, Please select the customer', 'error');
                return null;
            }

            // copy array and set id to requirementId as expected by the underwriting requirement update function
            const newValue = {...value};
            newValue.id = value.requirementId;
            const response = CustomerPortalAPI.UpdateUnderwritingRequirements({
                customerId: selectedCustomer?.id,
                newRequirements: [newValue],
            });

            // update array to show updates locally (only used when single req is updated)
            if (idx != null) arrayUpdateFunction({idx, value});

            return response;
        } catch (error: any) {
            console.log(error);
            RootStore.uiStore.showSnackbar('Something went wrong, Check Logs', 'error');
            return null;
        }
    }

    async function upsertRequirement(value: any) {
        try {
            if (!selectedCustomer?.id) {
                RootStore.uiStore.showSnackbar('Customer is not associated, Please select the customer', 'error');
                return null;
            }

            // copy array and set id to requirement_id as expected by the underwriting requirement update function
            const newValue = {...value};
            newValue.id = value.requirementId;
            const response = CustomerPortalAPI.UpdateUnderwritingRequirements({
                customerId: selectedCustomer?.id,
                newRequirements: [newValue],
            });

            return response;
        } catch (error: any) {
            console.log(error);
            RootStore.uiStore.showSnackbar('Something went wrong, Check Logs', 'error');
            return null;
        }
    }

    async function upsertThisRequirement(requirement: any) {
        let redirectUrl = null;
        if (requirement.type === RequirementPaymentType.PAYMENT_INFORMATION) {
            const paymentInfo = await upsertBankingInfo(requirement);
            if (paymentInfo) {
                const paymentInfoId = paymentInfo.id;
                // set payment info id in case of new requirement
                requirement.paymentInformationId = paymentInfoId;
                // update the requirement itself
                await upsertRequirement(requirement);
            }
        } else {
            // only need to upsert requirement and store response
            const isClicked = JSON.stringify(docusignModalRequirement.current) === JSON.stringify(requirement);
            requirement.needDocusign = true;
            requirement.dsReturnUrl = `${window.location.href}`;
            const response = await upsertRequirement(requirement);
            if (isClicked && response) redirectUrl = response.redirectUrl;
        }
        return redirectUrl;
    }

    async function upsertAllRequirements() {
        // insert requirements one by one in case we need to relate
        // a payment info id to a specific requirement
        // If requirement is the eftdocument requirement, save redirect URL

        // using map/filter to ensure all requirements are upserted before redirecting
        const redirectUrls = await Promise.all(
            [...requirementPaymentInfoArray, ...localRequirementPaymentInfoArray].map(async (thisRequirement) =>
                upsertThisRequirement(thisRequirement),
            ),
        );

        return redirectUrls.filter((redirectUrl) => redirectUrl !== null)[0];
    }

    const docusignUpsertAll = useCallback(
        (setLoading: (loading: boolean) => void) => {
            // first upsert general banking info
            upsertBankingInfo(generalPaymentInfo);
            upsertAllRequirements()
                .then((redirectUrl) => {
                    setRedirectUrl(redirectUrl);
                })
                .finally(() => {
                    setLoading(false);
                    setUpdateInfo((prev) => !prev);
                });
        },
        [generalPaymentInfo, docusignModalRequirement, requirementPaymentInfoArray, localRequirementPaymentInfoArray],
    );

    async function loadCustomerPaymentRequirements() {
        if (selectedCustomer?.id) {
            try {
                const requirements = [
                    ...((
                        await CustomerPortalAPI.GetCustomerRequirements(selectedCustomer?.id, {
                            type: RequirementPaymentType.PAYMENT_INFORMATION,
                        })
                    )?.requirements || []),
                    ...((
                        await CustomerPortalAPI.GetCustomerRequirements(selectedCustomer?.id, {
                            type: RequirementPaymentType.EFT_DOCUMENT,
                        })
                    )?.requirements || []),
                ];
                const paymentInfo = await CustomerPortalAPI.GetCustomerPaymentInfo(selectedCustomer?.id);
                if (!!requirements && !!paymentInfo) {
                    const requirementPaymentInfo = requirements.map((x: underwritingRequirementType) => {
                        // rename id field
                        x.requirementId = x.id;
                        delete x.id;
                        return Object.assign(
                            x,
                            paymentInfo.find((y) => y.id === x.paymentInformationId),
                        );
                    });
                    setRequirementPaymentInfoArray([...requirementPaymentInfo]);
                }
                setLocalRequirementPaymentInfoArray([]);
                RootStore.uiStore.showSnackbar(
                    `Successfully ${firstRender ? 'Loaded' : 'Updated'} Payment Info Requirements`,
                    'success',
                );
                setFirstRender(false);
            } catch (error: any) {
                console.log(error);
                RootStore.uiStore.showSnackbar('Something went wrong, Check Logs', 'error');
            }
        }
    }

    const handleAddRequirementClick = () => {
        setLocalRequirementPaymentInfoArray(
            (prev: any[]) =>
                [
                    ...prev,
                    {
                        customerId: '',
                        type: RequirementPaymentType.PAYMENT_INFORMATION,
                        status: RequirementStatus.OUTSTANDING,
                        title: 'Bank Information',
                        link: '',
                        additional_instructions: '',
                        internal_note: '',
                        file_title: '',
                        data: '',
                        private: true,
                    },
                ] as any[],
        );
    };

    const updateRequirementPaymentInfoArray = ({index, value}: {index: number; value: any}) => {
        // make copy of array and update value
        const copyArray = requirementPaymentInfoArray.slice();
        copyArray[index] = value;
        setRequirementPaymentInfoArray(copyArray);
    };

    const updateLocalRequirementPaymentInfoArray = ({index, value}: {index: number; value: any}) => {
        // make copy of array and update value
        const copyArray = localRequirementPaymentInfoArray.slice();
        copyArray[index] = value;
        setLocalRequirementPaymentInfoArray(copyArray);
    };

    const setDocusignModalRequirement = useCallback(
        (value: any) => {
            docusignModalRequirement.current = value;
        },
        [docusignModalRequirement],
    );

    async function sendEnvelope(envelopeId: string, lockToken?: string) {
        try {
            const sent = await CustomerPortalAPI.SendDocusignEnvelope(envelopeId, lockToken);
            if (sent) {
                console.log('SENT!!!!');
                console.log(sent);
                return;
            }
        } catch (error: any) {
            console.log(error);
            RootStore.uiStore.showSnackbar('Something went wrong, Check Logs', 'error');
        }
    }

    useEffect(() => {
        const searchParams = new URLSearchParams(search);
        const envelopeId = searchParams.get('envelopeId');
        const lockToken = searchParams.get('lockToken') ?? undefined;
        if (envelopeId) {
            searchParams.delete('envelopeId');
            searchParams.delete('lockToken');
            searchParams.delete('event');
            history.replace({pathname: location.pathname, search: searchParams.toString()});
            sendEnvelope(envelopeId, lockToken);
        }
    }, []);

    useEffect(() => {
        if (redirectUrl) window.location.href = redirectUrl;
    }, [redirectUrl]);

    useEffect(() => {
        loadCustomerPaymentRequirements();
        loadGeneralPaymentInformation();
    }, [updateInfo, selectedCustomer]);

    function BankingRequirement(props: BankingRequirementProps) {
        const {index, value, arrayUpdateFunction} = props;
        const [bankInfoDisplay, setBankInfoDisplay] = useState(
            value?.type === RequirementPaymentType.PAYMENT_INFORMATION,
        );

        const updateRequirementFunction = async (setLoading: (loading: boolean) => void) => {
            const response = await upsertRequirementAndUpdateArrays(index, value, arrayUpdateFunction);
            if (response?.id) value.requirementId = response.id;
            setLoading(false);
        };

        const updateBankingRequirementFunction = async ({
            childVal,
            setLoading,
        }: {
            childVal: any;
            setLoading: (loading: boolean) => void;
        }) => {
            const paymentInfoId = (await upsertBankingInfo(childVal))?.id;
            // set payment info id in case of new requirement
            value.paymentInformationId = paymentInfoId;
            // update the requirement itself
            await updateRequirementFunction(setLoading);
            setLoading(false);
        };

        return (
            <CaseManagerMenu index={index} value={value} showBankDisplay={setBankInfoDisplay}>
                {bankInfoDisplay ? (
                    <BankingInformationForm
                        index={index}
                        updateFunction={updateBankingRequirementFunction}
                        value={value}
                        customerEmail={selectedCustomer?.email}
                        customerGuid={selectedCustomer?.id}
                    />
                ) : (
                    <EFTDocumentForm
                        index={index}
                        updateRequirements={docusignUpsertAll}
                        setDocusignModalRequirement={setDocusignModalRequirement}
                        updateSingleRequirement={updateRequirementFunction}
                        value={value}
                    />
                )}
            </CaseManagerMenu>
        );
    }

    function BankingInformation() {
        return (
            <Grid item xs={7} md={8} lg={9} spacing={3} className="scroll-panel-left">
                <Grid container spacing={1} xs={12} className="left-panel-container">
                    <Grid item xs={12}>
                        <Grid container spacing={2} direction="row" alignItems="center">
                            <Grid item>
                                <h2>Banking Information</h2>
                            </Grid>
                            <Grid item>
                                <Tooltip disableFocusListener arrow placement="right" title={<BankTooltip />}>
                                    <InfoIcon color="disabled" fontSize="small" />
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} />
                </Grid>
                <Paper>
                    <div>
                        <Card className="banking-info-card-container">
                            <BankingInformationForm
                                index={-1}
                                updateFunction={upsertGeneralPaymentInfo}
                                value={generalPaymentInfo}
                                customerEmail={selectedCustomer?.email}
                                customerGuid={selectedCustomer?.id}
                            />
                        </Card>
                    </div>
                </Paper>
                {requirementPaymentInfoArray?.map((value: any, index: number) => (
                    <div
                        ref={(el) => {
                            ref.current[index] = el;
                        }}
                        key={Math.random().toString()}
                    >
                        <Card className="banking-info-card-container">
                            <BankingRequirement
                                index={index}
                                value={value}
                                arrayUpdateFunction={updateRequirementPaymentInfoArray}
                            />
                        </Card>
                    </div>
                ))}
                {localRequirementPaymentInfoArray?.map((value: any, index: number) => (
                    <div
                        ref={(el) => {
                            ref.current[index + requirementPaymentInfoArray.length] = el;
                        }}
                        key={Math.random().toString()}
                    >
                        <Card className="banking-info-card-container">
                            <BankingRequirement
                                index={index}
                                value={value}
                                arrayUpdateFunction={updateLocalRequirementPaymentInfoArray}
                            />
                        </Card>
                    </div>
                ))}
            </Grid>
        );
    }

    function BankingRequirementStatusPanel() {
        const classes = useStyles();

        return (
            <Grid
                container
                item
                xs={5}
                md={4}
                lg={3}
                style={{
                    backgroundColor: '#F8F8F8',
                    padding: '24px',
                }}
            >
                <Grid item xs={12} classes={{root: classes.requirementPanelRight}}>
                    <Box display="flex" justifyContent="space-between" alignContent="center" alignItems="center">
                        <Typography classes={{root: classes.requirementLabel}}>Requirements</Typography>
                        <Button type="button" onClick={handleAddRequirementClick}>
                            <Typography classes={{root: classes.requirementAddButton}}>+ add requirement</Typography>
                        </Button>
                    </Box>
                    {requirementPaymentInfoArray?.map((item: any) => (
                        <Paper key={Math.random().toString()} className="right-panel-bankinginfo-card">
                            <Box className="bankinginfo-card-content-container">
                                <Box>
                                    <Box className="right-requirement-bankinginfo-text">{`${
                                        BankingRequirementTypeOptions.filter(
                                            (type: {value: string; label: string}) => type.value === item.type,
                                        )[0]?.label
                                    }`}</Box>
                                    <Box className="right-time-bankinginfo-text">
                                        {`created - ${formatDate(item.insertedAt)}`}
                                    </Box>
                                    <Box className="right-time-bankinginfo-text right-time-bankinginfo-bottom-text">
                                        {`updated - ${formatDate(item.updatedAt)}`}
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                className="right-status-bankinginfo-text"
                                style={{
                                    backgroundColor:
                                        item.status === RequirementStatus.OUTSTANDING ? '#ffd000' : '#5be187',
                                    color: item.status === RequirementStatus.OUTSTANDING ? '#000' : '#389665',
                                }}
                            >
                                {`status - ${AllStatus.filter(
                                    (status) => status.name === item.status,
                                )[0]?.description?.toLowerCase()}`}
                            </Box>
                        </Paper>
                    ))}
                    {localRequirementPaymentInfoArray?.map((item: any) => (
                        <Paper key={Math.random().toString()} className="right-panel-bankinginfo-card">
                            <Box className="bankinginfo-card-content-container">
                                <Box>
                                    <Box className="right-requirement-bankinginfo-text">{`${
                                        BankingRequirementTypeOptions.filter(
                                            (type: {value: string; label: string}) => type.value === item.type,
                                        )[0]?.label
                                    }`}</Box>
                                    <Box className="right-time-bankinginfo-text">{}</Box>
                                    <Box className="right-time-bankinginfo-text right-time-bankinginfo-bottom-text">
                                        {}
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                className="right-status-bankinginfo-text"
                                style={{
                                    backgroundColor:
                                        item.status === RequirementStatus.OUTSTANDING ? '#ffd000' : '#5be187',
                                    color: item.status === RequirementStatus.OUTSTANDING ? '#000' : '#389665',
                                }}
                            >
                                {`status - ${AllStatus.filter(
                                    (status) => status.name === item.status,
                                )[0]?.description?.toLowerCase()}`}
                            </Box>
                        </Paper>
                    ))}
                </Grid>
            </Grid>
        );
    }
    return (
        <Grid container spacing={4} classes={{root: classes.wrapper}}>
            <BankingInformation />
            <BankingRequirementStatusPanel />
        </Grid>
    );
});

export default BankingPanel;
